<template>
  <div class="schedule-section mt-2">
    <div class="px-2">
      <p class="schedule-section-title">Assigned Professional</p>
    </div>
    <div class="section-card">
      <div v-if="appointment?.service_provider" class="pro-header pa-4 pb-0">
        <div class="pro-row">
          <div class="pro-avatar">
            <v-avatar size="40">
              <img
                v-if="appointment.service_provider.avatar"
                :src="appointment.service_provider.avatar"
                alt="Service provider avatar"
                loading="lazy"
              />
              <v-icon v-else size="18">mdi-account-outline</v-icon>
            </v-avatar>
          </div>
        <div class="pro-copy">
          <p class="pro-name">{{ appointment.service_provider.name || "-" }}</p>
          <p class="pro-meta">
            <v-icon size="14">mdi-email-outline</v-icon>
            <span>{{ appointment.service_provider.email || "-" }}</span>
            </p>
            <p class="pro-meta">
              <v-icon size="14">mdi-phone-outline</v-icon>
              <span>{{ formatPhoneNumber(appointment.service_provider.phone_no) || "-" }}</span>
            </p>
          </div>
        </div>
        <v-btn
          icon
          size="small"
          variant="tonal"
          class="pro-edit-btn"
          color="warning"
          aria-label="Edit assigned professional"
          :loading="isLoadingProviders"
          :disabled="isLoadingProviders"
          @click="handleAssignProfessional()"
        >
          <v-icon size="18">mdi-pencil-outline</v-icon>
        </v-btn>
      </div>
      <div v-if="appointment?.service_provider" class="summary-actions">
        <button
          type="button"
          class="summary-action"
          :class="{ 'summary-action--disabled': !providerEmailHref }"
          :disabled="!providerEmailHref"
          @click="emit('message')"
        >
          <v-icon size="18" color="deep-orange-darken-1">mdi-message-outline</v-icon>
          <span>Message</span>
        </button>
        <a
          class="summary-action summary-action--split"
          :class="{ 'summary-action--disabled': !providerEmailHref }"
          :href="providerEmailHref || undefined"
        >
          <v-icon size="18" color="deep-orange-darken-1">mdi-email-outline</v-icon>
          <span>Email</span>
        </a>
        <a
          class="summary-action summary-action--split"
          :class="{ 'summary-action--disabled': !providerPhoneHref }"
          :href="providerPhoneHref || undefined"
        >
          <v-icon size="18" color="deep-orange-darken-1">mdi-phone-outline</v-icon>
          <span>Call</span>
        </a>
      </div>
      <div v-else class="w-100">
        <NoProfessionalAssigned
          :appointment="appointment"
          @assigned="handleAssigned"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AssignProfessionalModal from "@/modules/vendor/components/appointments/AssignProfessionalModal.vue";
import NoProfessionalAssigned from "@/modules/vendor/components/appointments/NoProfessionalAssigned.vue";
import { computed } from "vue";
import { ref } from "vue";
import { formatPhoneNumber } from "@/shared/utils/phone";
import { useModalStore } from "@/shared/stores/modal";
import { fetchServiceProviders } from "@/modules/vendor/services/vendors.api";

type AppointmentAssignedProfessional = {
  vendor_id?: number | string | null;
  service_provider?: {
    avatar: string | null;
    name: string | null;
    email: string | null;
    phone_no: string | null;
  } | null;
};

const props = defineProps<{
  appointment: AppointmentAssignedProfessional;
}>();

const appointment = computed(() => props.appointment);

const emit = defineEmits<{
  (e: "assigned"): void;
  (e: "message"): void;
}>();

const modal = useModalStore();
const isLoadingProviders = ref(false);

const providerEmailHref = computed(() => {
  const email = props.appointment.service_provider?.email?.trim();
  return email ? `mailto:${email}` : "";
});

const providerPhoneHref = computed(() => {
  const phone = props.appointment.service_provider?.phone_no?.trim();
  return phone ? `tel:${phone}` : "";
});

function handleAssigned() {
  emit("assigned");
}

async function handleAssignProfessional() {
  const vendorId = props.appointment.vendor_id;
  isLoadingProviders.value = true;

  try {
    const providersResponse = vendorId ? await fetchServiceProviders(vendorId) : [];
    const providers = Array.isArray(providersResponse)
      ? providersResponse
      : Array.isArray((providersResponse as any)?.data)
        ? (providersResponse as any).data
        : [];

    modal.open(
      AssignProfessionalModal,
      {
        appointment: props.appointment,
        providers,
        onAssigned: () => {
          emit("assigned");
        },
      },
      { fullscreen: true, showHeader: false, title: "Assign Professional", scrollable: true },
    );
  } finally {
    isLoadingProviders.value = false;
  }
}
</script>

<style scoped>
.schedule-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.schedule-section-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #6b7280;
}

.section-card {
  overflow: hidden;
  background: #fff;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.summary-actions {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  border-top: 1px solid #e5e7eb;
}

.summary-action {
  min-height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  text-decoration: none;
}

.summary-action--split {
  border-left: 1px solid #e5e7eb;
}

.summary-action--disabled {
  pointer-events: none;
  opacity: 0.45;
}

.pro-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.pro-row {
  display: flex;
  gap: 12px;
  padding: 0 2px;
  align-items: center;
  min-width: 0;
}

.pro-avatar {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  overflow: hidden;
  background: rgba(249, 115, 22, 0.12);
  color: #f97316;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
}

.pro-name {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.pro-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pro-edit-btn {
  flex: 0 0 auto;
  margin-top: -2px;
}

.pro-meta {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: var(--color-subtitle-light);
}
</style>
